import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";

import { Widget } from "@/modules";
import { useAgentWebSocket } from "@/modules/websocket";
import { useAgentV2WebSocket } from "@/modules/websocket/hooks/useAgentV2WebSocket";
import { useAgentPocWebSocket } from "@/modules/websocket/hooks/useAgentPocWebSocket";
import { useDashboard } from "@/modules/dashboard/context/DashboardContext";
import { usePage } from "@/modules/pages/context/PageContext";
import { widgetEndpoints } from "@/modules/widgets/api/endpoints";
import { transformDraftToWidgetPayload } from "@/modules/widgets/utils/widgetTransform";
import { useAuth } from "@/modules/auth";
import { config } from "@/config/env";

interface UseAddWidgetDrawerParams {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (prompt: string) => void;
  onWidgetCreated?: (widget: Widget) => void;
  onPreviewReady?: (widget: Widget) => void;
  isLoading?: boolean;
  dashboardDate: { from: Date; to: Date };
}

interface ProgressUpdate {
  type: string;
  message: string;
}

type DrawerPanel = "ai" | "custom";

function buildWidgetSummary(widget: any): string {
  const parts: string[] = [];
  if (widget.title) parts.push(`"${widget.title}"`);
  if (widget.type) parts.push(widget.chartType ? `${widget.type}/${widget.chartType}` : widget.type);
  const level = widget.service?.parameters?.level;
  if (level) parts.push(`${level} level`);
  const segment = widget.service?.parameters?.segment;
  if (segment) parts.push(`by ${segment}`);
  if (widget.metrics?.length) parts.push(`metrics: ${widget.metrics.join(", ")}`);
  if (widget.datePeriod && widget.datePeriod !== "dashboard_date") parts.push(widget.datePeriod.replace(/_/g, " "));
  return parts.join(", ");
}

export const useAddWidgetDrawer = ({ isOpen, onClose, onSubmit, onWidgetCreated, onPreviewReady, isLoading = false, dashboardDate }: UseAddWidgetDrawerParams) => {
  const [prompt, setPrompt] = useState("");
  const [refinementPrompt, setRefinementPrompt] = useState("");
  const [isCreatingWidget, setIsCreatingWidget] = useState(false);
  const [panel, setPanel] = useState<DrawerPanel>("ai");
  const [publishToAll, setPublishToAll] = useState(false);
  const [agentFeedbackMessage, setAgentFeedbackMessage] = useState<string | undefined>(undefined);
  const [agentClarificationItems, setAgentClarificationItems] = useState<any[] | undefined>(undefined);
  const [previewWidget, setPreviewWidget] = useState<Widget | undefined>(undefined);
  const [conversationHistory, setConversationHistory] = useState<{ prompt: string; widgetSummary: string; widgetSpec?: any }[]>([]);
  const widgetCreated = useRef(false);
  const isPreviewMode = useRef(false);
  const traceMarkdownRef = useRef<string | undefined>(undefined);
  const traceEntriesRef = useRef<any[]>([]);
  const originalPromptRef = useRef<string>("");
  const promptRef = useRef<string>(prompt);
  promptRef.current = prompt;
  const previousProgressUpdatesRef = useRef<ProgressUpdate[]>([]);

  // All hooks must always be called — React forbids conditional hook calls.
  const v1 = useAgentWebSocket();
  const v2 = useAgentV2WebSocket();
  const poc = useAgentPocWebSocket();
  // POC takes priority over V2; V2 takes priority over V1
  const ws = config.AGENT_POC_ENABLED ? poc : config.AGENT_V2_ENABLED ? v2 : v1;
  // v2Like = the active hook that supports clarification + confirmPreview
  const v2Like = config.AGENT_POC_ENABLED ? poc : v2;

  const { isReady, isProcessing, progressMessages, messages, clearSessionMessages, resetProcessing } = ws;
  const resetSession = (ws as typeof v2).resetSession ?? clearSessionMessages;

  const clearSessionMessagesRef = useRef(clearSessionMessages);
  clearSessionMessagesRef.current = clearSessionMessages;
  const resetSessionRef = useRef(resetSession);
  resetSessionRef.current = resetSession;
  const resetProcessingRef = useRef(resetProcessing);
  resetProcessingRef.current = resetProcessing;
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  const { state: dashboardState } = useDashboard();
  const { state: pageState } = usePage();
  const { user } = useAuth();

  const currentDashboard = dashboardState.currentDashboard;
  const currentPage = pageState.currentPage;
  const currentTab = pageState.currentTab;

  const currentProgressUpdates = useMemo<ProgressUpdate[]>(() => {
    return progressMessages
      .map((msg: any) => {
        const agentMessage = msg?.data?.agentMessage;
        return {
          type: agentMessage?.type || "THOUGHT",
          message: agentMessage?.message || "Thinking...",
        };
      })
      .filter((update) => !update.message.trim().startsWith("<"));
  }, [progressMessages]);
  const currentProgressUpdatesRef = useRef<ProgressUpdate[]>([]);
  currentProgressUpdatesRef.current = currentProgressUpdates;
  const progressUpdates = useMemo<ProgressUpdate[]>(
    () => [...previousProgressUpdatesRef.current, ...currentProgressUpdates],
    [currentProgressUpdates],
  );

  useEffect(() => {
    if (!isOpenRef.current) return;
    const lastMessage = messages[messages.length - 1] as any;
    if (!lastMessage) {
      return;
    }

    const agentMessage = lastMessage?.data?.agentMessage;

    if (lastMessage?.data?.type === "in-progress" && agentMessage?.type === "PROMPT_REFINED") {
      setPrompt(agentMessage.message);
      return;
    }

    if (!agentMessage) {
      return;
    }

    if (lastMessage?.data?.type === "done" && !widgetCreated.current) {
      const donePayload = agentMessage as { lastWidget: Widget; agentResponse?: string; isExamplePreview?: boolean; pendingWidgetSpec?: any; selectionContext?: any };
      if (donePayload.selectionContext && Object.keys(donePayload.selectionContext).length > 0) {
        console.log("[Agent] selectionContext:", donePayload.selectionContext);
      }
      widgetCreated.current = true;
      setIsCreatingWidget(false);
      resetProcessingRef.current();
      previousProgressUpdatesRef.current = [...previousProgressUpdatesRef.current, ...currentProgressUpdatesRef.current];
      clearSessionMessagesRef.current();
      if (donePayload.isExamplePreview && donePayload.pendingWidgetSpec) {
        const spec = donePayload.pendingWidgetSpec;
        const summary = buildWidgetSummary(spec);
        traceMarkdownRef.current = (donePayload as any).traceMarkdown ?? undefined;
        setConversationHistory(prev => [...prev, { prompt: promptRef.current, widgetSummary: summary, widgetSpec: spec }]);
        setPreviewWidget(spec);
      } else if (donePayload.lastWidget) {
        const summary = buildWidgetSummary(donePayload.lastWidget);
        setConversationHistory(prev => [...prev, { prompt: promptRef.current, widgetSummary: summary, widgetSpec: donePayload.lastWidget }]);
        if (isPreviewMode.current && onPreviewReady) {
          isPreviewMode.current = false;
          onPreviewReady(donePayload.lastWidget);
        } else if (onWidgetCreated) {
          onWidgetCreated(donePayload.lastWidget);
        }
      } else {
        const items = (donePayload as any).clarificationItems as any[] | undefined;
        if (items?.length) {
          // Store trace entries so they're forwarded on the resume call, giving the
          // backend the full history from the first invocation.
          traceEntriesRef.current = (donePayload as any).traceEntries ?? [];
          setAgentClarificationItems(items);
          originalPromptRef.current = promptRef.current;
        } else {
          // Agent returned plain text instead of ask_user — treat it as a freeText
          // clarification so the user gets a proper reply input instead of a dead end.
          const agentText = donePayload.agentResponse || "Could you add a bit more detail? That'll help me build the right widget for you.";
          traceEntriesRef.current = (donePayload as any).traceEntries ?? [];
          setAgentClarificationItems([{
            title: "Agent",
            message: agentText,
            options: [],
            preselected: [],
            multi: false,
            freeText: true,
          }]);
          originalPromptRef.current = promptRef.current;
        }
      }
    }

    if (lastMessage?.data?.type === "error") {
      setIsCreatingWidget(false);
      resetProcessingRef.current();
      clearSessionMessagesRef.current();
      setAgentFeedbackMessage("Something went wrong. Please try again.");
    }
  }, [messages, onWidgetCreated, onPreviewReady]);

  useEffect(() => {
    if (!isOpen) {
      setPrompt("");
      setRefinementPrompt("");
      setIsCreatingWidget(false);
      setPanel("ai");
      setPublishToAll(false);
      setAgentFeedbackMessage(undefined);
      setAgentClarificationItems(undefined);
      setPreviewWidget(undefined);
      setConversationHistory([]);
      widgetCreated.current = false;
      traceMarkdownRef.current = undefined;
      traceEntriesRef.current = [];
      previousProgressUpdatesRef.current = [];
      resetSessionRef.current();
    }
  }, [isOpen]);

  const handleConfirm = useCallback(async () => {
    const isRefinement = Boolean(previewWidget);
    const basePrompt = prompt.trim();
    const refinement = refinementPrompt.trim();
    if (!basePrompt || (isRefinement && !refinement) || !currentDashboard || !currentPage) {
      return;
    }
    if (!isReady) {
      setAgentFeedbackMessage("Reconnecting to the server — please try again in a moment.");
      return;
    }

    widgetCreated.current = false;
    setAgentFeedbackMessage(undefined);
    setAgentClarificationItems(undefined);
    setPreviewWidget(undefined);
    if (isRefinement) {
      setRefinementPrompt("");
    }
    setIsCreatingWidget(true);
    originalPromptRef.current = basePrompt;

    try {
      if (config.AGENT_POC_ENABLED || config.AGENT_V2_ENABLED) {
        // For refinement, send the refinement text as the new prompt; conversationHistory
        // carries the previous widget spec so the agent has full context.
        const effectivePrompt = isRefinement ? refinement : basePrompt;
        await v2Like.sendRequest(
          effectivePrompt,
          Number(dashboardState?.selectedDealer?.dealerRefId ?? 0),
          currentDashboard.id,
          currentPage.id,
          currentTab?.tags?.[0] || undefined,
          user?.userId,
          publishToAll,
          currentPage.title,
          undefined,
          currentTab?.tabPrompt || undefined,
          conversationHistory.length ? conversationHistory : undefined,
          { from: dashboardDate.from.toISOString(), to: dashboardDate.to.toISOString() },
        );
      } else {
        v1.sendRequest(
          basePrompt,
          Number(dashboardState?.selectedDealer?.dealerRefId ?? 0),
          currentDashboard.id,
          currentPage.id,
          currentTab?.tags?.[0] || undefined,
          user?.userId,
          publishToAll,
        );
      }

      if (onSubmit) {
        onSubmit(basePrompt);
      }
    } catch (err) {
      setIsCreatingWidget(false);
      console.error("Failed to create widget. Please try again.", err);
    }
  }, [prompt, refinementPrompt, previewWidget, currentDashboard, currentPage, isReady, onSubmit, user, currentTab, publishToAll, dashboardState, v1, v2Like]);

  const handleClarificationConfirm = useCallback((answers: Array<
    | { title: string; values: string[] }
    | { title: string; action: "accept" | "reject"; filters?: unknown[] }
  >) => {
    if (!answers.length) return;
    setAgentClarificationItems(undefined);
    setAgentFeedbackMessage(undefined);
    widgetCreated.current = false;
    setIsCreatingWidget(true);
    // Resume the paused graph with the user's answer — same sessionId, no graph restart.
    // For the POC, also forward trace entries from the first invocation so the backend
    // can restore the full session history.
    if (config.AGENT_POC_ENABLED) {
      poc.sendClarificationAnswer(answers[0], traceEntriesRef.current);
      traceEntriesRef.current = [];
    } else {
      v2Like.sendClarificationAnswer(answers[0]);
    }
  }, [v2Like, poc]);

  // Reject path for the filter-confirmation bubble. Tells the agent the user
  // bailed (graph routes to END without saving), restores the original prompt
  // so the user can refine and resubmit.
  const handleFilterRejection = useCallback(() => {
    v2Like.sendClarificationAnswer({ title: "Confirm filters", action: "reject" });
    setAgentClarificationItems(undefined);
    setAgentFeedbackMessage(undefined);
    setPreviewWidget(undefined);
    setRefinementPrompt("");
    setIsCreatingWidget(false);
    widgetCreated.current = false;
    setPrompt(originalPromptRef.current);
    resetSessionRef.current();
    resetProcessingRef.current();
  }, [v2Like]);

  const handlePreviewAccept = useCallback(() => {
    if (!previewWidget) return;
    const spec = previewWidget;
    const traceMarkdown = traceMarkdownRef.current;
    traceMarkdownRef.current = undefined;
    setPreviewWidget(undefined);
    setIsCreatingWidget(true);
    widgetCreated.current = false;
    v2Like.confirmPreview(
      spec,
      Number(dashboardState?.selectedDealer?.dealerRefId ?? 0),
      currentDashboard!.id,
      currentPage!.id,
      currentTab?.tags?.[0] || undefined,
      user?.userId,
      publishToAll,
      traceMarkdown,
    );
  }, [previewWidget, v2Like, dashboardState, currentDashboard, currentPage, currentTab, user, publishToAll]);

  const handlePreviewDiscard = useCallback(() => {
    setPreviewWidget(undefined);
    setRefinementPrompt("");
    widgetCreated.current = false;
  }, []);

  const handleCancel = useCallback(() => {
    setPrompt("");
    setRefinementPrompt("");
    setPreviewWidget(undefined);
    setAgentFeedbackMessage(undefined);
    setAgentClarificationItems(undefined);
    setIsCreatingWidget(false);
    setConversationHistory([]);
    widgetCreated.current = false;
    resetSessionRef.current();
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        void handleConfirm();
      }

      if (event.key === "Escape") {
        handleCancel();
      }
    },
    [handleConfirm, handleCancel]
  );

  const handlePromptChange = useCallback((value: string) => {
    setPrompt(value);
    widgetCreated.current = false;
  }, []);

  const handleRefinementPromptChange = useCallback((value: string) => {
    setRefinementPrompt(value);
    widgetCreated.current = false;
  }, []);

  const goToCustomPanel = useCallback(() => {
    setPanel("custom");
  }, []);

  const goToAIPanel = useCallback(() => {
    setPanel("ai");
  }, []);

  const handleCreateManualWidget = useCallback(
    async (draft: any, hierarchyData?: { levels: any[]; segments: any[]; metrics: any[]; products: any[] }) => {
      if (!currentDashboard || !currentPage) {
        console.error("Dashboard or page not available");
        return;
      }

      setIsCreatingWidget(true);
      try {
        const payload = transformDraftToWidgetPayload(
          draft,
          currentPage.id,
          currentTab || null,
          hierarchyData?.levels || [],
          hierarchyData?.segments || [],
          hierarchyData?.metrics || [],
          hierarchyData?.products || [],
          currentDashboard.clientId,
          {
            pageDateRange: dashboardDate,
            userId: user?.userId,
          }
        );

        // Preview mode: render a synthetic widget locally without writing to the DB.
        // The /widget-data endpoint reads service/metrics/parameters from the body and
        // does not require a saved widget row, so a temp id (-1) is sufficient.
        if (isPreviewMode.current) {
          console.log("### PREVIEWING WIDGET (no DB write)", {
            currentTab,
            payload,
            currentPageId: currentPage.id,
            currentDashboardId: currentDashboard.id,
          });
          const now = new Date().toISOString();
          const syntheticWidget = {
            ...payload,
            id: -1,
            subtitle: "",
            prompt: "",
            fullyAIGenerated: false,
            createdAt: now,
            updatedAt: now,
            data: undefined,
          } as unknown as Widget;
          if (onPreviewReady) {
            isPreviewMode.current = false;
            onPreviewReady(syntheticWidget);
          }
          setIsCreatingWidget(false);
          return;
        }

        console.log("### CREATING WIDGET", {
          currentTab,
          payload,
          currentPageId: currentPage.id,
          currentDashboardId: currentDashboard.id,
        });
        const response = await widgetEndpoints.createManualWidget(payload);

        if (response.widget && onWidgetCreated) {
          onWidgetCreated(response.widget);
        }

        setIsCreatingWidget(false);
      } catch (err) {
        setIsCreatingWidget(false);
        console.error("Failed to create manual widget:", err);
      }
    },
    [currentDashboard, currentPage, currentTab, onWidgetCreated, onPreviewReady, dashboardDate, user]
  );

  const setPreviewMode = useCallback((value: boolean) => {
    isPreviewMode.current = value;
  }, []);

  const hasClarification = !!agentClarificationItems?.length;
  const isButtonDisabled = hasClarification
    ? true
    : agentFeedbackMessage
      ? !prompt.trim()
      : previewWidget
        ? !refinementPrompt.trim() || isCreatingWidget || isProcessing
        // Don't gate on the parent's `isLoading` — it's set on submit and only cleared on
        // widget-created or drawer-close, so the button stays stuck disabled after a reject.
        // isCreatingWidget tracks the real local creation state and is cleared correctly.
        : !prompt.trim() || isCreatingWidget || isProcessing || !isReady;
  const buttonText = isCreatingWidget || isProcessing ? "Creating..." : "Create Widget";

  return {
    prompt,
    handlePromptChange,
    refinementPrompt,
    handleRefinementPromptChange,
    handleConfirm,
    handleClarificationConfirm,
    handleFilterRejection,
    handleCancel,
    handleKeyDown,
    isButtonDisabled,
    buttonText,
    isCreatingWidget,
    isProcessing,
    isReady,
    currentDashboard,
    currentPage,
    progressUpdates,
    panel,
    goToCustomPanel,
    goToAIPanel,
    handleCreateManualWidget,
    publishToAll,
    setPublishToAll,
    agentFeedbackMessage,
    agentClarificationItems,
    previewWidget,
    handlePreviewAccept,
    handlePreviewDiscard,
    setPreviewMode,
    conversationHistory,
  };
};
